/**
 * Numbers and timestamps, formatted the same way everywhere and on every
 * machine.
 *
 * `value.toLocaleString()` with no locale uses whatever the host happens to be
 * set to. On the machine this was written on that is Indian grouping, where
 * 612904 renders as "6,12,904" — so the same screen showed different numbers to
 * different people, and a test asserting "612,904" passed or failed depending
 * on who ran it.
 *
 * Pinned to en-GB because the product's own English is British: a screen that
 * says "organisation" and then groups its figures by another convention is two
 * voices in one interface. It is a deliberate choice rather than an inherited
 * default, which is the whole difference.
 */
const COUNT = new Intl.NumberFormat('en-GB')

export function count(value: number): string {
  return COUNT.format(value)
}

/**
 * A rate as a percentage, to one decimal.
 *
 * Bounce and complaint rates live near thresholds like 5% and 0.1%, so the
 * decimal is the difference between "under" and "over" rather than decoration.
 */
export function percent(rate: number, decimals = 1): string {
  return `${(rate * 100).toFixed(decimals)}%`
}

/**
 * A timestamp, in the viewer's own zone.
 *
 * When something happened is evidence — when an address was suppressed, when a
 * key was last used — so it is shown in full rather than as "3 days ago". A
 * relative time is friendlier and useless to anybody reconciling against a
 * provider's log.
 *
 * Pinned for the same reason the numbers are: an unpinned format renders
 * differently per machine, which is how a test starts passing or failing
 * depending on who ran it.
 */
const STAMP = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' })

export function when(iso: string): string {
  return STAMP.format(new Date(iso))
}
